import { useState } from 'react';
import * as theme from '../../../theme';
import TruCard from '../../TruCard';
import CreditCardItem from '../items/CreditCardItem';
import { Box, Button, Modal, Typography, FormControlLabel, Checkbox, TextField } from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import dayjs from 'dayjs';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { updateOnboardingStep } from '../../../functions/updateOnboarding';
import StateSelect from '../../StateSelect';

export default function CreditCardList({ practitionerId, billing, currentUser, setError, getPractitioner, practitioner }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [cardType, setCardType] = useState(null); // New state for card type: 'business' or 'personal'
  const [selectedCard, setSelectedCard] = useState(null);
  const [consent, setConsent] = useState(false);
  const [nonceError, setNonceError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('step1');
  const [nonce, setNonce] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    cardholderName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: ''
  });

  const handleAction = (action, type = null, card = null) => {
    setCurrentAction(action);
    setCardType(type);
    setSelectedCard(card);
    setModalOpen(true);
    setConsent(false);
    setNonceError(null);
    setLoading(false);
    setStep('step1');
    setNonce(null);
    setBillingInfo({
      cardholderName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: ''
    });
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedCard(null);
    setCurrentAction(null);
    setCardType(null);
    setConsent(false);
    setNonceError(null);
    setLoading(false);
    setStep('step1');
    setNonce(null);
    setBillingInfo({
      cardholderName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: ''
    });
  };

  const handleBillingInfoSubmit = () => {
    const { cardholderName, addressLine1, city, state, zip } = billingInfo;
    if (!cardholderName || !addressLine1 || !city || !state || !zip) {
      setNonceError('Please fill in all required billing information.');
      return;
    }
    setNonceError(null);
    setStep('step2');
  };

  const handleSaveCard = async () => {
    if (!consent) {
      setNonceError('You must agree to save your card.');
      return;
    }
    setLoading(true);
    try {
      const functions = getFunctions();
      const saveCard = httpsCallable(functions, 'saveCard');
      const result = await saveCard({
        nonce,
        practitionerId,
        cardholderName: billingInfo.cardholderName,
        billingAddress: {
          addressLine1: billingInfo.addressLine1,
          addressLine2: billingInfo.addressLine2,
          city: billingInfo.city,
          state: billingInfo.state,
          zip: billingInfo.zip
        },
        type: cardType // Pass the card type to the backend
      });
      if (result.data.success) {
        // Update cardOnFile if not already set (assuming this is primarily for business, but can adjust logic if needed)
        if (!practitioner?.onboarding?.cardOnFile?.dateCompleted) {
          const { success, practitioner: updatedPractitioner } = await updateOnboardingStep({
            practitionerId,
            step: 'cardOnFile',
          });
          if (success) {
            // Continue
          }
        }

        getPractitioner();
        setNonceError(null);
        handleClose();
      } else {
        throw new Error('Failed to save card');
      }
    } catch (error) {
      setNonceError(`Failed to save card: ${error.message}`);
      console.error('Error calling saveCard:', error);
    }
    setLoading(false);
  };

  // Helper to get cards by type, treating undefined type as 'business' for legacy cards
  const getCardsByType = (type) => {
    return billing.cards && billing.cards.length > 0
      ? [...billing.cards].filter(card => (card.type || 'business') === type).sort((a, b) => {
          // Prioritize enabled card
          if (a.enabled && !b.enabled) return -1;
          if (!a.enabled && b.enabled) return 1;
          // Sort disabled cards by dateModified descending (most recent first)
          const dateA = new Date(a.dateModified);
          const dateB = new Date(b.dateModified);
          return dateB - dateA;
        })
      : [];
  };

  const businessCards = getCardsByType('business');
  const personalCards = getCardsByType('personal');

  const hasActiveBusiness = businessCards.some(card => card.enabled);
  const hasActivePersonal = personalCards.some(card => card.enabled);

  return (
    <TruCard title={'Payment Methods'}>
      <div style={{ ...theme.Container.flexColumn, gap: 20 }}>
        <div>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Business Card</Typography>
          {businessCards.length > 0 ? (
            businessCards.map((card, index) => (
              card.enabled ? (
                <CreditCardItem 
                  key={index}
                  card={card}
                  actionLink={() => handleAction('cardDetails', null, card)}
                />
              ) : (
                <CreditCardItem 
                  key={index}
                  card={card}
                />
              )
            ))
          ) : (
            <div style={theme.TruCard.SubText}>No business payment method added</div>
          )}
        </div>
        <div>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Personal Card</Typography>
          {personalCards.length > 0 ? (
            personalCards.map((card, index) => (
              card.enabled ? (
                <CreditCardItem 
                  key={index}
                  card={card}
                  actionLink={() => handleAction('cardDetails', null, card)}
                />
              ) : (
                <CreditCardItem 
                  key={index}
                  card={card}
                />
              )
            ))
          ) : (
            <div style={theme.TruCard.SubText}>No personal payment method added</div>
          )}
        </div>
      </div>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: '20px' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => handleAction('addCard', 'business')}
        >
          <AddCardIcon /><span style={{ marginLeft: 10 }}>{hasActiveBusiness ? 'Replace' : 'Add'} Business Card</span>
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => handleAction('addCard', 'personal')}
        >
          <AddCardIcon /><span style={{ marginLeft: 10 }}>{hasActivePersonal ? 'Replace' : 'Add'} Personal Card</span>
        </Button>
      </Box>
      {modalOpen && currentAction && (
        <Modal
          open={modalOpen}
          onClose={handleClose}
          aria-labelledby="card-modal-title"
          aria-describedby="card-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: 'none',
            '&:focus-visible': {
              boxShadow: `0 0 0 2px ${theme.Colors.Primary}`,
            },
          }}>
            {currentAction === 'cardDetails' && selectedCard ? (
              <>
                <Typography id="card-modal-title" variant="h6" gutterBottom>
                  Card Details
                </Typography>
                <Typography variant="body1">
                  <strong>Type:</strong> {selectedCard.type === 'business' ? 'Business' : 'Personal'}
                </Typography>
                <Typography variant="body1">
                  <strong>Brand:</strong> {selectedCard.brand}
                </Typography>
                <Typography variant="body1">
                  <strong>Last 4 Digits:</strong> x{selectedCard.last4}
                </Typography>
                <Typography variant="body1">
                  <strong>Expiration:</strong> {String(selectedCard.expMonth).padStart(2, '0')}/{String(selectedCard.expYear).slice(-2)}
                </Typography>
                <Typography variant="body1">
                  <strong>Date Added:</strong> {selectedCard.dateAdded ? dayjs(selectedCard.dateAdded.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Date Modified:</strong> {selectedCard.dateModified ? dayjs(selectedCard.dateModified.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}
                </Typography><br/>
                <Typography variant="body1">
                  <strong>Card On File Agreement:</strong> {selectedCard.agreementText}<br/>
                  <strong>Date Signed:</strong> {selectedCard.dateSigned ? dayjs(selectedCard.dateAdded.toDate()).format('MMMM D, YYYY h:mm A') : 'N/A'}<br/>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  This card is for {selectedCard.type === 'business' ? 'business' : 'personal'} use. You can have one active card per type. To replace this card, add a new card for this type.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button variant="contained" onClick={handleClose}>
                    Close
                  </Button>
                </Box>
              </>
            ) : currentAction === 'addCard' ? (
              <>
                <Typography id="card-modal-title" variant="h6" gutterBottom>
                  {step === 'step1' ? `Billing Information for ${cardType.charAt(0).toUpperCase() + cardType.slice(1)} Card` : step === 'step2' ? `Add New ${cardType.charAt(0).toUpperCase() + cardType.slice(1)} Card` : 'Authorize Card Storage'}
                </Typography>
                {nonceError && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {nonceError}
                  </Typography>
                )}
                {step === 'step1' ? (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Please provide the billing information for the {cardType} card.
                    </Typography>
                    <TextField
                      label="Cardholder Name"
                      value={billingInfo.cardholderName}
                      onChange={(e) => setBillingInfo({ ...billingInfo, cardholderName: e.target.value })}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Address Line 1"
                      value={billingInfo.addressLine1}
                      onChange={(e) => setBillingInfo({ ...billingInfo, addressLine1: e.target.value })}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Address Line 2"
                      value={billingInfo.addressLine2}
                      onChange={(e) => setBillingInfo({ ...billingInfo, addressLine2: e.target.value })}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="City"
                      value={billingInfo.city}
                      onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                      fullWidth
                      required
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
                      <StateSelect 
                        value={billingInfo.state} 
                        onChange={(e) => setBillingInfo({ ...billingInfo, state: e.target.value })}
                        variant='outlined'
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Zip Code"
                        value={billingInfo.zip}
                        onChange={(e) => setBillingInfo({ ...billingInfo, zip: e.target.value })}
                        required
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button variant="outlined" onClick={handleClose} sx={{ mr: 2 }}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBillingInfoSubmit}
                        disabled={loading}
                      >
                        Continue
                      </Button>
                    </Box>
                  </>
                ) : step === 'step2' ? (
                  <>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Enter your card details to verify. You will not be charged.
                    </Typography>
                    <PaymentForm
                      applicationId={process.env.REACT_APP_SQUARE_APP_ID}
                      locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                      cardTokenizeResponseReceived={(token, verifiedBuyer) => {
                        setLoading(false);
                        if (token.status === 'OK') {
                          setNonce(token.token);
                          setNonceError(null);
                          setStep('step3');
                        } else {
                          const errorDetails = token.errors ? token.errors.map(err => ({
                            code: err.code,
                            message: err.message,
                            type: err.type
                          })) : token;
                          setNonceError('Failed to generate card nonce: Verification failed. Please check card and billing details.');
                          console.error('Tokenization error details:', errorDetails);
                        }
                      }}
                      createVerificationDetails={() => ({
                        intent: 'STORE',
                        billingContact: {
                          addressLines: [billingInfo.addressLine1, billingInfo.addressLine2 || ''],
                          countryCode: 'US',
                          region: billingInfo.state,
                          postalCode: billingInfo.zip,
                          city: billingInfo.city
                        }
                      })}
                    >
                      <CreditCard
                        buttonProps={{
                          isLoading: loading,
                          disabled: loading,
                          css: {
                            backgroundColor: theme.Colors.GoldDark,
                            color: theme.Colors.GrayDark,
                            fontSize: 14,
                            '&:hover': {
                              backgroundColor: '#8A7B5D',
                            },
                          },
                        }}
                      >VERIFY CARD</CreditCard>
                    </PaymentForm>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button variant="outlined" onClick={handleClose}>
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ p: 2, border: 1, borderColor: theme.Colors.GrayLight, borderRadius: 1, mt: 2 }}>
                      <div style={theme.SubHeadingText}>
                        I authorize this business to keep my credit card on file for any future transactions until I request a cancellation.<br/><br/>
                        I understand that the terms of this authorization will remain in effect until I request that they be terminated. I acknowledge that I am responsible for making this request in writing.
                      </div>
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="I agree to save my card for future transactions."
                      sx={{ mt: 2, mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{ mr: 2 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveCard}
                        disabled={!consent || loading}
                      >
                        {loading ? 'Processing...' : 'Save Card'}
                      </Button>
                    </Box>
                  </>
                )}
              </>
            ) : null}
          </Box>
        </Modal>
      )}
    </TruCard>
  );
}